import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { AxiosResponse } from 'axios'
import { pick } from 'lodash'

import orderBy from 'lodash/orderBy'

import { CashFlowProjection } from '@/entities/cashflow-projections/utils/types'
import { DuckDBSchemaType } from '@/services/duckdb/utils/types'
import { ApiListResponse } from '../utils/types'

import { CASHFLOW_PROJECTION_FIELD } from '@/const'

import {
  addStoreListItem,
  prepareResponseError,
  removeStoreListItem,
  updateStoreListItem,
} from '../utils/helpers'

import { DuckDBService } from '@/services/duckdb'

import api from '@/store/api'
import { useRepositoriesStore } from '../repositories'

export const TABLE_NAME = 'cashflow_projections'

const TABLE_SCHEMA: Record<string, DuckDBSchemaType> = {
  [CASHFLOW_PROJECTION_FIELD.ID]: 'VARCHAR',
  [CASHFLOW_PROJECTION_FIELD.ACCOUNT_ID]: 'VARCHAR',
  [CASHFLOW_PROJECTION_FIELD.ASSET_ID]: 'VARCHAR',
  [CASHFLOW_PROJECTION_FIELD.DATE]: 'DATE',
  [CASHFLOW_PROJECTION_FIELD.VALUE]: 'DOUBLE',
}

export const useCashFlowProjectionsStore = defineStore('cashflow-projections', () => {
  // INIT
  const duckdbInstance = DuckDBService.instance()

  const repositoriesStore = useRepositoriesStore()

  const list = ref<CashFlowProjection[]>([])

  const initFlag = ref<boolean>(false)
  const trigger = ref(0)

  const loading = ref<boolean>(true)
  const loadingAction = ref<boolean>(false)

  let abortController = new AbortController()

  const repositoryId = computed(() => repositoriesStore.getCurrentId)

  // GETTERS
  const getList = computed<CashFlowProjection[]>(() =>
    orderBy(list.value, [item => item.date]),
  )

  // SETTERS

  const setList = async (data: CashFlowProjection[]) => {
    list.value = data
    await duckdbInstance.createTable(TABLE_NAME, TABLE_SCHEMA)
    await duckdbInstance.pushData(TABLE_NAME, data, TABLE_SCHEMA)
    trigger.value++
  }

  const fetch = async (): Promise<CashFlowProjection[]> => {
    loading.value = true
    try {
      const result = await api.get<ApiListResponse<CashFlowProjection[]>>('cashflows', {
        params: {
          repository_id: repositoryId.value,
        },
        signal: abortController.signal,
      })
      const data = result.data.data
      await setList(data)
      initFlag.value = true
      return data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loading.value = false
    }
  }

  const prepareData = (data: Partial<CashFlowProjection>, ...additional: string[]): Partial<CashFlowProjection> => {
    return pick(data, 'account_id', 'asset_id', 'date', 'value', 'description', ...additional)
  }

  const store = async (data: CashFlowProjection): Promise<CashFlowProjection> => {
    loadingAction.value = true
    try {
      const result = await api.post<CashFlowProjection, AxiosResponse<CashFlowProjection>, Partial<CashFlowProjection>>(
        `cashflows?repository_id=${repositoryId.value}`,
        { ...prepareData(data), repository_id: repositoryId.value },
      )
      await duckdbInstance.pushData(TABLE_NAME, [result.data], TABLE_SCHEMA)
      return result.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const update = async (data: CashFlowProjection): Promise<CashFlowProjection | undefined> => {
    loadingAction.value = true
    try {
      const { id } = data
      const result = await api.put<CashFlowProjection, AxiosResponse<CashFlowProjection>, Partial<CashFlowProjection>>(
        `cashflows/${id}`,
        prepareData(data, 'id'),
      )
      updateStoreListItem<CashFlowProjection>(list.value, result.data)
      await duckdbInstance.updateData(TABLE_NAME, id, result.data, TABLE_SCHEMA)
      return data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const destroy = async (id: string): Promise<void> => {
    loadingAction.value = true
    try {
      await api.delete(`cashflows/${id}?repository_id=${repositoryId.value}`)
      removeStoreListItem<CashFlowProjection>(list.value, id)
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const bulkStore = async (
    cashflows: Partial<CashFlowProjection>[],
  ): Promise<CashFlowProjection[]> => {
    loadingAction.value = true
    try {
      const result = await api.post<CashFlowProjection[], { data: AxiosResponse<CashFlowProjection[]> }, Partial<CashFlowProjection>[]>(
        `cashflows?repository_id=${repositoryId.value}`,
        cashflows.map(cashflow => ({
          ...prepareData(cashflow),
          repository_id: repositoryId.value,
        })),
      )
      result.data.data.forEach(cashflow => {
        addStoreListItem<CashFlowProjection>(list.value, cashflow)
      })
      return result.data.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const bulkUpdate = async (
    cashflows: Partial<CashFlowProjection>[],
  ): Promise<void> => {
    loadingAction.value = true
    try {
      const result = await api.put<CashFlowProjection[], { data: AxiosResponse<CashFlowProjection[]> }, Partial<CashFlowProjection>[]>(
        `cashflows?repository_id=${repositoryId.value}`,
        cashflows.map(cashflow => prepareData(cashflow, 'id')),
      )
      result.data.data.forEach(cashflow => {
        updateStoreListItem<CashFlowProjection>(list.value, cashflow)
      })
    } catch (e) {
      throw Error(e as string)
    } finally {
      loadingAction.value = false
    }
  }

  const bulkDestroy = async (ids: string[]): Promise<void> => {
    loadingAction.value = true
    try {
      await api.delete('cashflows', { data: ids })
      ids.forEach(id => {
        removeStoreListItem<CashFlowProjection>(list.value, id)
      })
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const cancel = () => {
    abortController.abort()
    abortController = new AbortController()
  }

  const clear = () => {
    initFlag.value = false
    list.value = []
  }

  return {
    initFlag,
    trigger,
    loading,
    loadingAction,

    getList,

    fetch,

    store,
    update,
    destroy,

    bulkStore,
    bulkUpdate,
    bulkDestroy,

    cancel,
    clear,
  }
})
